import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { formatUserLevel } from '@/utils/formatters';
import { cn } from '@/lib/utils';
import { diagnosisService } from '@/services/diagnosisService';
import { statisticsService, DashboardStatisticsResponse } from '@/services/statisticsService';
import { patientService } from '@/services/patientService';
import { Patient } from '@/types/patient.types';
import { ViewPatientDialog } from '@/components/forms/PatientList';
import { PatientForm } from '@/components/forms/PatientForm';
import {
  Heart,
  Users,
  FileText,
  Calendar,
  Stethoscope,
  BarChart3,
  PlusCircle,
  Maximize2,
  Minimize2,
  UserPlus,
  X
} from 'lucide-react';

export const HospitalDashboard: React.FC = () => {
  const { utilizator } = useAuth();
  const [diagnosisCount, setDiagnosisCount] = useState<number>(0);
  const [dashboardStats, setDashboardStats] = useState<DashboardStatisticsResponse | null>(null);
  const [recentPatients, setRecentPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const loadDashboardData = async () => {
    try {
      // Load diagnosis count for current hospital
      // The backend already filters by hospital based on user permissions
      const diagnoses = await diagnosisService.getAllDiagnoses();
      if (Array.isArray(diagnoses)) {
        setDiagnosisCount(diagnoses.length);
      } else if (diagnoses && typeof diagnoses === 'object' && 'data' in diagnoses) {
        const diagnosesWithData = diagnoses as { data: any[] };
        setDiagnosisCount(Array.isArray(diagnosesWithData.data) ? diagnosesWithData.data.length : 0);
      } else {
        setDiagnosisCount(0);
      }

      // Load dashboard statistics for current hospital
      // Backend automatically filters by user's hospital context
      setStatsLoading(true);
      const stats = await statisticsService.getDashboardStatistics();
      setDashboardStats(stats);

      // Load recent patients for current hospital
      try {
        const patientsResponse = await patientService.getPatients({ limit: 5 });
        setRecentPatients(patientsResponse.data || []);
      } catch (error) {
        console.error('Error loading recent patients:', error);
        setRecentPatients([]);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setDiagnosisCount(0);
      setDashboardStats(null);
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (utilizator) {
      loadDashboardData();
    }
  }, [utilizator]);

  if (!utilizator) {
    return null;
  }

  const stats = [
    {
      title: 'Pacienți MCC activi',
      value: statsLoading ? '...' : (dashboardStats?.patients?.total || 0).toString(),
      change: dashboardStats?.patients?.this_year ? `+${dashboardStats.patients.this_year}` : '+0',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Diagnostice',
      value: loading ? '...' : (diagnosisCount || 0).toString(),
      change: diagnosisCount > 0 ? `+${Math.round((diagnosisCount / 10) * 100)}%` : '+0%',
      icon: Heart,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
    {
      title: 'Proceduri efectuate',
      value: statsLoading ? '...' : (dashboardStats?.procedures?.total || 0).toString(),
      change: dashboardStats?.procedures?.this_year ? `+${dashboardStats.procedures.this_year}` : '+0',
      icon: Stethoscope,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Copii prematuri',
      value: statsLoading ? '...' : (dashboardStats?.patients?.premature_cases || 0).toString(),
      change: dashboardStats?.patients?.total ? `${((dashboardStats.patients.premature_cases / (dashboardStats.patients.total || 1)) * 100).toFixed(1)}%` : '0%',
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ];

  const quickActions = [
    {
      title: 'Caz nou',
      description: 'Adaugă un nou caz MCC identificat',
      icon: PlusCircle,
      action: 'new-case',
      color: 'bg-blue-500',
    },
    {
      title: 'Pacienți',
      description: 'Gestionează pacienții MCC',
      icon: Users,
      action: 'patients',
      color: 'bg-green-500',
    },
    {
      title: 'Diagnostice',
      description: 'Gestionare diagnostice MCC',
      icon: Heart,
      action: 'diagnoses',
      color: 'bg-red-500',
    },
    {
      title: 'Proceduri medicale',
      description: 'Înregistrează proceduri efectuate',
      icon: Stethoscope,
      action: 'procedures',
      color: 'bg-purple-500',
    },
    {
      title: 'Rapoarte clinice',
      description: 'Generează rapoarte detaliate',
      icon: FileText,
      action: 'clinical-reports',
      color: 'bg-orange-500',
    },
    {
      title: 'Statistici',
      description: 'Vizualizează statistici spital',
      icon: BarChart3,
      action: 'statistics',
      color: 'bg-indigo-500',
    },
  ];


  const handleQuickAction = (action: string) => {
    const event = new CustomEvent('navigate', { detail: { page: action } });
    window.dispatchEvent(event);
  };

  const handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedPatient(null);
  };

  return (
    <>
      <MainLayout>
        <div className="space-y-6">
          {/* Welcome Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Tablou de Bord - Spital
              </h1>
              <p className="text-muted-foreground">
                Bine ați venit, {utilizator.name || utilizator.username || 'utilizator'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {utilizator.hospital_name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {formatUserLevel(utilizator.user_level?.id || utilizator.user_level_id)}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {utilizator.hospital_name}
              </Badge>
              <Button variant="outline" size="sm">
                <Calendar className="mr-2 h-4 w-4" />
                Astăzi
              </Button>
              <Button size="sm">
                <FileText className="mr-2 h-4 w-4" />
                Raport rapid
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <Card key={stat.title}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {stat.title}
                    </CardTitle>
                    <div className={`rounded-full p-2 ${stat.bgColor}`}>
                      <Icon className={`h-4 w-4 ${stat.color}`} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                    {stat.change && (
                      <p className="text-xs text-muted-foreground">
                        <span className="text-green-600">{stat.change}</span> față de luna trecută
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Quick Actions Grid */}
          <div className="w-full">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Acțiuni rapide</h2>
              <p className="text-muted-foreground">Acces rapid la funcționalitățile principale</p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 justify-items-center max-w-6xl mx-auto">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Card
                    key={action.action}
                    className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 hover:-translate-y-1 w-full max-w-xs"
                    onClick={() => handleQuickAction(action.action)}
                  >
                    <CardContent className="p-6 text-center flex flex-col items-center justify-center h-40">
                      <div className={`rounded-xl p-4 ${action.color} text-white shadow-lg mb-4`}>
                        <Icon className="h-8 w-8" />
                      </div>
                      <h3 className="font-bold text-sm mb-2 text-gray-800">{action.title}</h3>
                      <p className="text-xs text-muted-foreground text-center leading-relaxed">
                        {action.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Full Width Layout */}
          <div className="grid gap-6">
            {/* Recent Patients */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Pacienți recenți
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleQuickAction('patients')}
                  >
                    Vezi toți
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentPatients.length > 0 ? (
                  <div className="space-y-4">
                    {recentPatients.map((patient) => (
                      <div key={patient.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => handlePatientClick(patient)}>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-600 hover:text-blue-800">{patient.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Cod: {patient.patient_code} • Născut: {new Date(patient.birth_date).toLocaleDateString('ro-RO')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {patient.gender === 0 ? '♀ Feminin' : '♂ Masculin'} • Adăugat: {new Date(patient.created_at).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">
                            {patient.hospital_name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    Nu există pacienți recenți
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Alerts & Notifications - Commented out */}
            {/*
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  Alerte și Notificări
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Atenție</p>
                      <p className="text-xs text-muted-foreground">
                        3 pacienți necesită follow-up în această săptămână
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <Activity className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Reminder</p>
                      <p className="text-xs text-muted-foreground">
                        Raportul lunar EUROCAT trebuie trimis până pe 30
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            */}
          </div>
        </div>

        {/* View Patient Dialog */}
        <ViewPatientDialog
          patient={selectedPatient}
          isOpen={viewDialogOpen}
          onClose={handleCloseViewDialog}
        />
      </MainLayout>
    </>
  );
};

